import { Component, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { PaypalService } from '../../_services/paypal.service';

declare var paypal: any;

export interface PaypalResponseDataType {
  status: boolean,
  data: any,
  errorType: string
}

@Component({
  selector: 'app-paypal-button',
  template: `<div id="paypal-button-container"></div><div id="paypal-card-button-container"></div>`,
  standalone: true
})
export class PaypalButtonComponent implements AfterViewInit {

  private static readonly CARD_BUTTON_ENABLED = false;

  @Input() totalAmount: any;
  @Input() payerInfo: any;
  @Output() paypalResponseRecieved = new EventEmitter<PaypalResponseDataType>();
  @Output() paypalButtonClicked = new EventEmitter<void>();
  // Emitted when the "PayPal" (wallet) button is clicked - the SDK's own
  // approval flow is cancelled for this funding source, and the parent is
  // expected to submit the order itself (no upfront capture, pay-by-link).
  @Output() paypalWalletClicked = new EventEmitter<void>();

  paypalResponse: PaypalResponseDataType = {
    status: false,
    data: {},
    errorType: 'TRY_AGAIN'
  };

  constructor(private paypalService: PaypalService) {}

  private buildPayer(): any {
    const info = this.payerInfo;
    if (!info) return undefined;

    const payer: any = {
      name: {
        given_name: info.firstName || '',
        surname: info.lastName || '',
      },
      email_address: info.email || '',
      address: {
        address_line_1: info.address1 || '',
        address_line_2: info.address2 || '',
        admin_area_2: info.city || '',
        postal_code: info.postCode || '',
        country_code: 'GB',
      },
    };
    if (info.phoneNo) {
      payer.phone = {
        phone_type: 'MOBILE',
        phone_number: { national_number: info.phoneNo },
      };
    }
    return payer;
  }

  ngAfterViewInit(): void {
    this.paypalService.load().then(() => this.renderButtons());
  }

  private renderButtons(): void {
    const self = this;

    const createOrder = function (data: any, actions: any) {
      // Read totalAmount live at click time, not once at render time - it's
      // an @Input bound to the parent's calculated total, which can still be
      // 0 (cart still loading) at the moment this button first renders.
      // Snapshotting it here would lock the button into submitting £0
      // forever, which PayPal rejects with CANNOT_BE_ZERO_OR_NEGATIVE.
      const payer = self.buildPayer();
      return actions.order.create({
        purchase_units: [{
          amount: {
            value: self.totalAmount?.toString()
          }
        }],
        ...(payer ? { payer } : {}),
      });
    };
    const onApprove = function (data: any, actions: any) {
      self.payPalPaymentApproved(data);
    };
    const onCancel = function (data: any) {
      self.paypalPaymentCancelled();
    };
    const onError = function (err: any) {
      self.paypalPaymentFailed();
    };

    paypal.Buttons({
      fundingSource: paypal.FUNDING.PAYPAL,
      createOrder,
      onClick: function (data: any, actions: any) {
        // Staff can't complete the customer's PayPal login on their
        // behalf - place the order instead and let the customer pay via
        // the emailed payment link.
        self.paypalWalletClicked.emit();
        return actions.reject();
      },
      onApprove,
      onCancel,
      onError
    }).render('#paypal-button-container');

    // PayPal's "Debit or Credit Card" button is disabled for now.
    if (PaypalButtonComponent.CARD_BUTTON_ENABLED) {
      const cardButtons = paypal.Buttons({
        fundingSource: paypal.FUNDING.CARD,
        createOrder,
        onClick: function (data: any, actions: any) {
          self.payPalButtonClicked();
          return actions.resolve();
        },
        onApprove,
        onCancel,
        onError
      });
      if (cardButtons.isEligible()) {
        cardButtons.render('#paypal-card-button-container');
      }
    }
  }

  private payPalButtonClicked() {
    this.paypalButtonClicked.emit();
  }

  private payPalPaymentApproved(paypalData: any) {
    this.paypalResponse = {
      status: true,
      data: paypalData,
      errorType: ''
    };
    this.paypalResponseRecieved.emit(this.paypalResponse);
  }

  private paypalPaymentCancelled() {
    this.paypalResponse = {
      status: false,
      data: {},
      errorType: 'CUSTOMER_CANCELLED'
    }
    this.paypalResponseRecieved.emit(this.paypalResponse);
  }

  private paypalPaymentFailed() {
    this.paypalResponse = {
      status: false,
      data: {},
      errorType: 'SOMETHING_WENT_WRONG'
    }
    this.paypalResponseRecieved.emit(this.paypalResponse);
  }

}
